var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-tree-view.ts
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { switchMap } from "rxjs";
import { ChemistryLitElement } from "../../chemistry-lit-element.js";
import { RXController } from "../../controllers/RXController.js";
import { css } from "../../directives/css.js";
import { virtualize } from "../../directives/when-seen.js";
import { xComponents } from "../x-components.js";
import "./x-card.js";
import "./x-flat-loader.js";
import "./x-link.js";
async function getChildren(node) {
  return node.children ? typeof node.children === "function" ? await node.children(node) ?? [] : node.children : [];
}
var XTreeView = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.data = new RXController(this, [
      { title: "Pass in .data: TreeNode" },
      { title: "Pass in .data: TreeNode" }
    ]);
    this.extendedIndices = /* @__PURE__ */ new Set();
    this.nested = false;
    this.allowMultipleExtended = false;
    this.calculatedTreeNodes = new RXController(
      this,
      this.data.observable.pipe(
        switchMap(
          async (nodes) => Promise.all(nodes.map(async (node) => ({ ...node, computedChildren: await getChildren(node) })))
        )
      )
    );
  }
  render() {
    if (this.nested)
      return this.content();
    return html` <x-basic-card> ${this.content()} </x-basic-card> `;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("calculatedTreeNodes") && this.calculatedTreeNodes.value) {
      for (let i = 0; i < this.calculatedTreeNodes.value.length; i++) {
        const node = this.calculatedTreeNodes.value[i];
        if (node.extended && !this.extendedIndices.has(i))
          this.extendedIndices.add(i);
      }
      this.requestUpdate();
    }
  }
  content() {
    return html`
      ${repeat(
      this.calculatedTreeNodes.value ?? [],
      (node) => node.title,
      (node, i) => {
        return virtualize(
          xComponents(html`
              ${this.extendedIndices.has(i) && !!node.computedChildren?.length ? html` <x-hr></x-hr> ` : nothing}
              <x-flex
                flex-none
                space="xs"
                ${css({ borderTop: i === 0 ? "" : `1px solid ${this.colors.neutral.shade(100)}` })}
                @click=${() => this.handleItemClick(i, node)}
              >
                ${this.renderNode(node, i)}
              </x-flex>

              ${this.extendedIndices.has(i) && !!node.computedChildren?.length ? html`
                    <x-tree-view
                      flex-none
                      nested
                      ${css({
            paddingLeft: `${this.spacing.xs.number + 20}px`,
            borderTop: `1px solid ${this.colors.neutral.shade(100)}`
          })}
                      .data=${node.computedChildren}
                      .handleItemClick=${this.handleItemClick}
                      .renderNode=${this.renderNode}
                      .renderIcon=${this.renderIcon}
                      .renderContent=${this.renderContent}
                    ></x-tree-view>
                    <x-hr></x-hr>
                  ` : nothing}
            `),
          (oldHeight) => html` <x-flat-loader ${css({ height: `${oldHeight}px`, width: "100%" })}></x-flat-loader> `,
          // TODO shim
          { onRender: () => this.dispatchEvent(new CustomEvent("item-rendered", { detail: { value: i } })) }
        );
      }
    )}
    `;
  }
  styles() {
    return [{ ":host": { display: "block" } }];
  }
  handleItemClick(i, node) {
    if (!!node.computedChildren?.length) {
      if (this.allowMultipleExtended) {
        if (this.extendedIndices.has(i))
          this.extendedIndices.delete(i);
        else
          this.extendedIndices.add(i);
      } else {
        if (!this.extendedIndices.has(i)) {
          this.extendedIndices.clear();
          this.extendedIndices.add(i);
        } else
          this.extendedIndices.delete(i);
      }
      this.requestUpdate();
    }
    this.dispatchEvent(
      new CustomEvent("node-clicked", {
        detail: { node, open: this.extendedIndices.has(i) },
        bubbles: true,
        composed: true
      })
    );
  }
  renderNode(node, i) {
    const nodeView = xComponents(html`
      <x-flex
        horizontal
        crossaxis-center
        space="xs"
        padding="xs"
        ${css({ cursor: "pointer", background: node.active ? this.colors.neutral.shade(100) : "unset" })}
      >
        ${this.renderIcon(node, i)} ${this.renderContent(node, i)}
      </x-flex>
    `);
    if (node.href)
      return html` <x-link href=${node.href}> ${nodeView} </x-link> `;
    else
      return nodeView;
  }
  renderIcon(node, i) {
    return node.computedChildren?.length || node.icon ? html`
          <x-text ${css({ textAlign: "center" })}>
            <x-icon ${css({ minWidth: "20px" })}>
              ${!node.computedChildren?.length ? node.icon : this.extendedIndices.has(i) ? "keyboard_arrow_down" : "keyboard_arrow_right"}
            </x-icon>
          </x-text>
        ` : nothing;
  }
  renderContent(node, i) {
    return node.renderItem ? node.renderItem(node) : node.subtitle ? html`
          <x-flex space="xs">
            <x-text> ${node.title} </x-text>
            <x-text tone="subtle"> ${node.subtitle} </x-text>
          </x-flex>
        ` : html` <x-text> ${node.title} </x-text> `;
  }
};
__decorateClass([
  property()
], XTreeView.prototype, "data", 2);
__decorateClass([
  property()
], XTreeView.prototype, "extendedIndices", 2);
__decorateClass([
  property({ type: Boolean, attribute: true })
], XTreeView.prototype, "nested", 2);
__decorateClass([
  property({ type: Boolean, attribute: "allow-multiple-extended" })
], XTreeView.prototype, "allowMultipleExtended", 2);
__decorateClass([
  state()
], XTreeView.prototype, "calculatedTreeNodes", 2);
XTreeView = __decorateClass([
  customElement("x-tree-view")
], XTreeView);
export {
  XTreeView
};
